import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import isObject from 'lodash/isObject';

import { isGlobal } from 'Internals/pipes';
import { currentUserIsAdmin } from 'Redux/thunks/subscriptions.thunks';

import { TestID } from '../testID';

import type { Pipe } from 'src/types/pipes.types';
import type { RootState } from '../types/state.types';
import type { DataEntityTab } from 'src/types/dataEntitiesTabs.types';

type AllEntityTypeIds = { [key: string]: boolean };

export const usePipesTabs = (args: { subId: string; pipe?: Pipe; sinkDatasetID?: string }) => {
  const { subId, pipe, sinkDatasetID } = args;

  const isAdminRole = useSelector((state: RootState) => currentUserIsAdmin(state));
  const models = useSelector((state: RootState) => state.models);

  const allEntityTypeIds: AllEntityTypeIds = useMemo(() => {
    const ids: AllEntityTypeIds = {};

    for (const model of models) {
      const modelEntityTypes = model['entity_types'];

      if (modelEntityTypes && isObject(modelEntityTypes)) {
        for (const entityTypeId of Object.keys(modelEntityTypes)) {
          ids[entityTypeId] = true;
        }
      }
    }

    return ids;
  }, [models, sinkDatasetID, pipe]);

  const hasPipeSinkEntityType = allEntityTypeIds[pipe?._id ?? ''];
  const isPipeGlobal = pipe ? isGlobal(pipe) : false;
  const link = pipe?.link;
  const basePath = `/subscription/${subId}/pipes/pipe/${link}`;
  const hasDeadLetter = !!pipe?.config?.effective?.pump?.dead_letter_dataset;

  const pipeTabs: DataEntityTab[] = useMemo(
    () => [
      {
        id: 'dashboard',
        label: 'Dashboard',
        to: `${basePath}/dashboard`,
        tooltip: 'Dashboard tab',
        testId: TestID.PipeDashboardTab,
        shouldDisplay: true,
      },
      {
        id: 'config',
        label: 'Config',
        to: `${basePath}/edit`,
        tooltip: 'Config tab',
        testId: TestID.PipeConfigTab,
        shouldDisplay: true,
      },
      {
        id: 'input',
        label: 'Input',
        to: `${basePath}/input`,
        tooltip: 'Input tab',
        testId: TestID.PipeInputTab,
        shouldDisplay: true,
      },
      {
        id: 'output',
        label: 'Output',
        to: `${basePath}/output`,
        tooltip: 'Output tab',
        testId: TestID.PipeOutputTab,
        shouldDisplay: true,
      },
      {
        id: 'inferred-schema',
        label: 'Inferred schema',
        to: `${basePath}/inferred-schema`,
        tooltip: 'Inferred schema tab',
        testId: TestID.PipeInferredSchemaTab,
        shouldDisplay: !!hasPipeSinkEntityType,
      },
      {
        id: 'dead-letters',
        label: 'Dead letters',
        to: `${basePath}/dead-letters`,
        tooltip: 'Dead letters tab',
        testId: TestID.PipeDeadLettersTab,
        shouldDisplay: hasDeadLetter,
      },
      {
        id: 'execution-log',
        label: 'Execution log',
        to: `${basePath}/execution`,
        tooltip: 'Execution tab',
        testId: TestID.PipeExecutionLogTab,
        shouldDisplay: true,
      },
      {
        id: 'permissions',
        label: 'Permissions',
        to: `${basePath}/permissions`,
        tooltip: 'Permissions tab',
        testId: TestID.PipePermissionsTab,
        shouldDisplay: true,
      },
      {
        id: 'graph',
        label: 'Graph',
        to: `${basePath}/graph`,
        tooltip: 'Graph tab',
        testId: TestID.PipeGraphTab,
        shouldDisplay: true,
      },
      {
        id: 'indexes',
        label: 'Indexes',
        to: `${basePath}/indexes`,
        tooltip: 'Indexes tab',
        testId: TestID.PipeIndexesTab,
        shouldDisplay: !!sinkDatasetID,
      },
      {
        id: 'overview',
        label: 'Overview',
        to: `${basePath}/overview`,
        tooltip: 'Overview tab',
        testId: TestID.PipeOverviewTab,
        shouldDisplay: isPipeGlobal,
      },
      {
        id: 'debug',
        label: 'Debug',
        to: `${basePath}/debug`,
        tooltip: 'Debug tab',
        testId: TestID.PipeDebugTab,
        shouldDisplay: isAdminRole,
      },
    ],
    [hasPipeSinkEntityType, isPipeGlobal, isAdminRole, hasDeadLetter, basePath]
  );

  return pipeTabs ?? [];
};
